import { datadogRum } from '@newfront-insurance/dd-rum';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { NovaChatHostContextProvider, type NovaChatContextBackend } from './context';
import { NovaChatOverlay, type NovaChatOverlayProps, type NovaLoaderAuthContext } from './nova-chat-overlay';
import type { NovaHostContextPatch } from './protocol';

export type NovaChatOverlayProviderProps<TAuthContext extends NovaLoaderAuthContext = NovaLoaderAuthContext> = Omit<
  NovaChatOverlayProps<TAuthContext>,
  'contextPatch'
> & { children: ReactNode };

/** Stable host context and telemetry for Nova's remote launcher and chat. */
export function NovaChatOverlayProvider<TAuthContext extends NovaLoaderAuthContext>({
  children,
  hostApp,
  onTelemetry,
  onFatalError,
  ...overlayProps
}: NovaChatOverlayProviderProps<TAuthContext>) {
  const [contextPatch, setContextPatch] = useState<NovaHostContextPatch>({});
  const backend = useMemo<NovaChatContextBackend>(
    () => ({ setContextPatch: (patch) => setContextPatch((previous) => ({ ...previous, ...patch })) }),
    [],
  );
  const handleTelemetry = useCallback<NonNullable<NovaChatOverlayProps['onTelemetry']>>(
    (event) => {
      datadogRum.addAction(`nova_embed.${event.name}`, { hostApp, ...event.data });
      onTelemetry?.(event);
    },
    [hostApp, onTelemetry],
  );
  const handleFatalError = useCallback<NonNullable<NovaChatOverlayProps['onFatalError']>>(
    (reason) => {
      datadogRum.addAction('nova_embed.unavailable', { hostApp, reason });
      onFatalError?.(reason);
    },
    [hostApp, onFatalError],
  );

  return (
    <NovaChatHostContextProvider backend={backend}>
      {children}
      <NovaChatOverlay
        {...overlayProps}
        hostApp={hostApp}
        contextPatch={contextPatch}
        onTelemetry={handleTelemetry}
        onFatalError={handleFatalError}
      />
    </NovaChatHostContextProvider>
  );
}
